import React, { memo, useCallback } from 'react';
import { PopperProps, SvgIconProps, SxProps, Theme } from '@mui/material';

import { Dropdown, DropdownProps, DropdownOption } from '../Dropdown';
import { MiniClose24 } from '../../icons';
import {
    DropdownButton,
    DropdownButtonProps,
    useOpenState,
} from '../DropdownButton';


export type DropdownSelectProps<T extends DropdownProps['value'] = DropdownOption> = Pick<
DropdownButtonProps,
'disabled' | 'text' | 'size'
> &
Pick<DropdownProps, 'inputValue' | 'onInputChange'> & {
    selected: T;
    onSelect: (value: T) => void;
    options: DropdownOption[];
    onOpen?: () => void;
    onClose?: () => void;
    isMulti?: boolean;
    popperProps?: Pick<PopperProps, 'disablePortal' | 'sx'> & { dropdownSx?: SxProps<Theme> };
    isLoading?: boolean;
    buttonSx?: SxProps<Theme>;
    clearable?: boolean;
    wrapperButtonSx?: SxProps<Theme>;
};


const DropdownSelectComponent = <SType extends DropdownProps['value'] = DropdownOption>({
    selected, onSelect, options, onOpen, onClose, isMulti, popperProps = {},
    buttonSx, clearable, wrapperButtonSx, ...props
}: DropdownSelectProps<SType>) => {
    const openState = useOpenState(onOpen, onClose);
    const { dropdownSx = {}, ...restPopperProps } = popperProps;

    const isSelected = Array.isArray(selected) ? selected.length > 0 : !!selected;

    const onChange = useCallback<DropdownSelectProps<SType>['onSelect']>(
        value => {
            onSelect(value);

            if (!isMulti)
                openState.close();
        },
        [onSelect, openState, isMulti],
    );

    const onClear = useCallback<SvgIconProps['onClick']>(
        event => {
            event.stopPropagation();

            onChange(isMulti ? [] as SType : null);
        },
        [onChange, isMulti],
    );


    return (
        <DropdownButton
            isOpen={openState.isOpen}
            isActive={!!isSelected}
            onClick={openState.change}
            close={openState.close}
            endIcon={
                isSelected && clearable && (
                    <MiniClose24
                        sx={({ palette }) => ({
                            zIndex: 2,
                            color: palette.grayscale.shade100,
                        })}
                        onClick={onClear}
                    />
                )
            }
            fullWidth
            sx={{
                whiteSpace: ['nowrap'],
                borderRadius: 3,
                ...buttonSx,
            }}
            typographyProps={{
                sx: {
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                },
            }}
            popperProps={restPopperProps}
            wrapperButtonSx={wrapperButtonSx}
            {...props}
        >
            <Dropdown
                value={isMulti && !selected ? [] : selected}
                isMulti={isMulti}
                // @ts-ignore
                onChange={onChange}
                onMouseLeave={openState.close}
                options={options}
                sx={{
                    maxHeight: ({ spacing }) => spacing(99),
                    width: '100%',
                    borderRadius: 3,
                    ...dropdownSx,
                }}
                itemTypographyProps={{
                    variant: 'regularCaption1',
                }}
                itemSx={{
                    backgroundColor: 'transparent',
                }}
            />
        </DropdownButton>
    );
};

export const DropdownSelect = memo(DropdownSelectComponent) as typeof DropdownSelectComponent;
